import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtTRY, isKurumsal } from "@/lib/api";
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { Printer, Wallet, Building2, User } from "lucide-react";

export default function Reports() {
  const [balances, setBalances] = useState({ rows: [], toplam_bakiye: 0 });
  const [monthly, setMonthly] = useState([]);
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/reports/balances").then((r) => setBalances(r.data));
    api.get("/dashboard/monthly").then((r) => setMonthly(r.data));
    api.get("/dashboard/stats").then((r) => setStats(r.data));
  }, []);

  const pieData = stats ? [
    { name: "Bireysel", value: stats.tur_dagilim.bireysel },
    { name: "Kurumsal", value: stats.tur_dagilim.kurumsal },
  ] : [];
  const PIE_COLORS = ["#3b82f6", "#eab308"];

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Raporlar</h1>
          <p className="text-zinc-500 text-sm mt-1">Cari bakiye, gelir ve müşteri dağılım raporları</p>
        </div>
        <Button data-testid="print-btn" onClick={() => window.print()} variant="outline" className="rounded-none border-zinc-700 hover:bg-zinc-900">
          <Printer className="w-4 h-4 mr-1.5" /> Yazdır
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div className="bg-zinc-950 border border-zinc-800 p-5">
          <h2 className="text-base font-semibold mb-1">Aylık Gelir (Tahsilat vs Tahakkuk)</h2>
          <p className="text-xs text-zinc-500 mb-5">Son 6 ay (₺)</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={monthly} barGap={6}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
              <XAxis dataKey="ay" stroke="#71717a" fontSize={12} tickLine={false} axisLine={{ stroke: "#27272a" }} />
              <YAxis stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} width={70} tickFormatter={(v) => new Intl.NumberFormat("tr-TR", { notation: "compact" }).format(v)} />
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 0, color: "#fff" }} formatter={(v) => fmtTRY(v)} cursor={{ fill: "#ffffff08" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="tahakkuk" name="Tahakkuk" fill="#3f3f46" />
              <Bar dataKey="tahsilat" name="Tahsilat" fill="#22c55e" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-zinc-950 border border-zinc-800 p-5">
          <h2 className="text-base font-semibold mb-1">Müşteri Dağılımı</h2>
          <p className="text-xs text-zinc-500 mb-5">Bireysel / Kurumsal</p>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                {pieData.map((e, i) => <Cell key={i} fill={PIE_COLORS[i]} stroke="#09090b" />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 0, color: "#fff" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-zinc-950 border border-zinc-800">
        <div className="px-5 py-4 border-b border-zinc-800 flex items-center gap-2">
          <Wallet className="w-4 h-4 text-yellow-400" />
          <h2 className="text-base font-semibold">Cari Bakiye Raporu</h2>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400 uppercase text-[11px] tracking-widest">Müşteri</TableHead>
              <TableHead className="text-zinc-400 uppercase text-[11px] tracking-widest">Tip</TableHead>
              <TableHead className="text-zinc-400 uppercase text-[11px] tracking-widest text-right">Aylık Ücret</TableHead>
              <TableHead className="text-zinc-400 uppercase text-[11px] tracking-widest text-right">Bakiye</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {balances.rows.length === 0 && (
              <TableRow className="border-zinc-800 hover:bg-transparent"><TableCell colSpan={4} className="text-center py-12 text-zinc-500 text-sm">Kayıt yok.</TableCell></TableRow>
            )}
            {balances.rows.map((r) => (
              <TableRow key={r.id} data-testid={`report-row-${r.id}`} onClick={() => navigate(`/musteriler/${r.id}`)} className="border-zinc-900 cursor-pointer hover:bg-zinc-900">
                <TableCell className="py-3">
                  <div className="font-medium flex items-center gap-2">
                    {isKurumsal(r.tur) ? <Building2 className="w-3.5 h-3.5 text-zinc-500" /> : <User className="w-3.5 h-3.5 text-zinc-500" />}
                    {r.unvan || "İsimsiz"}
                  </div>
                  {r.musteri_kodu && <div className="text-xs text-zinc-500 font-mono ml-5">{r.musteri_kodu}</div>}
                </TableCell>
                <TableCell><Badge variant="outline" className="rounded-none text-[10px] uppercase tracking-wider border-zinc-700 text-zinc-300">{r.tur ? (isKurumsal(r.tur) ? "Kurumsal" : "Bireysel") : "-"}</Badge></TableCell>
                <TableCell className="text-right font-mono text-sm text-zinc-400">{r.aylik_hizmet_ucreti ? fmtTRY(r.aylik_hizmet_ucreti) : "-"}</TableCell>
                <TableCell className="text-right font-mono text-sm">
                  <span className={r.bakiye > 0 ? "text-yellow-400" : r.bakiye < 0 ? "text-green-400" : "text-zinc-400"}>{fmtTRY(r.bakiye)}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          {balances.rows.length > 0 && (
            <TableFooter className="bg-zinc-900/50">
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableCell colSpan={3} className="font-semibold uppercase text-xs tracking-widest text-zinc-400">Toplam Alacak</TableCell>
                <TableCell className="text-right font-mono font-semibold text-yellow-400" data-testid="report-total">{fmtTRY(balances.toplam_bakiye)}</TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </div>
    </div>
  );
}
